import { useFullscreenElement, useMergedRef } from '@mantine/hooks';
import { IconArrowsMaximize, IconArrowsMinimize, IconBoltFilled, IconDownload } from '@tabler/icons-react';
import cx from 'clsx';
import { stagger, useAnimate } from 'motion/react';
import { Suspense, useEffect, useEffectEvent } from 'react';

import { ComponentSkeleton } from '#core/components/dashboard/panels/shared/BoxSkeleton.tsx';
import { ErrorBoundaryWrapper } from '#core/components/error/ErrorBoundaryWrapper.tsx';
import { IconActionButton } from '#core/components/shared/IconActionButton.tsx';
import { DashboardExportContext, type TileExportEntry } from '#core/contexts/DashboardExportContext.ts';
import { type KeyedRegistry, useKeyedRegistry } from '#core/hooks/useKeyedRegistry.ts';
import type { TileSpec } from '#core/lib/expr/ops/dashboard/value.ts';
import type { DashboardSpec } from '#core/lib/expr/value.ts';
import { downloadCsvZip } from '#core/lib/transforms/download-csv-zip.ts';
import { slugify } from '#core/lib/utils/slugify.ts';

import { CartesianPanel } from './CartesianPanel.tsx';
import classes from './DashboardSpecRenderer.module.css';
import { DonutPanel } from './DonutPanel.tsx';
import { MarkdownPanel } from './MarkdownPanel.tsx';
import { MetricPanel } from './MetricPanel.tsx';
import { TablePanel } from './TablePanel.tsx';
import { TreemapPanel } from './TreemapPanel.tsx';

// biome-ignore lint/suspicious/noExplicitAny: various types
const TILE_COMPONENTS: Record<TileSpec['kind'], React.FC<{ spec: any; citedMarkers?: ReadonlySet<string> }>> = {
  'metric-spec': MetricPanel,
  'cartesian-spec': CartesianPanel,
  'table-spec': TablePanel,
  'donut-spec': DonutPanel,
  'treemap-spec': TreemapPanel,
  'markdown-spec': MarkdownPanel,
};

/** V-shape-native tile dispatch. Exhaustive over `TileSpec.kind`. */
function TileRenderer({
  tile,
  column,
  row,
  citedMarkers,
}: {
  tile: TileSpec;
  column?: number;
  row?: number;
  citedMarkers?: ReadonlySet<string>;
}) {
  const Component = TILE_COMPONENTS[tile.kind] ?? (() => <div>Unknown tile type: {tile.kind}</div>);
  return (
    <div className={cx(classes.gridItem)} style={{ gridColumn: `span ${column}`, gridRow: `span ${row}` }}>
      <ErrorBoundaryWrapper className={classes.gridItemError} title={`Failed to render ${tile.kind}`} variant="inline">
        <Suspense fallback={<ComponentSkeleton type={tile.kind} />}>
          <Component citedMarkers={citedMarkers} spec={tile} />
        </Suspense>
      </ErrorBoundaryWrapper>
    </div>
  );
}

export interface DashboardSpecRendererProps {
  readonly spec: DashboardSpec;
  /**
   * Caller-owned tile-export registry. Defaults to an internally-created
   * one when omitted. Pass an external registry to hoist the snapshot
   * one level up — e.g. the chat plugin threads its own registry so it
   * can aggregate every dashboard rendered in a single assistant message.
   */
  readonly registry?: KeyedRegistry<TileExportEntry>;
  /**
   * Labels of reference lines/areas cited by insights elsewhere in the
   * conversation — cartesian tiles render those markers emphasized.
   */
  readonly citedMarkers?: ReadonlySet<string>;
}

export function DashboardSpecRenderer({ spec, registry: external, citedMarkers }: DashboardSpecRendererProps) {
  const internal = useKeyedRegistry<TileExportEntry>();
  const registry = external ?? internal;
  const { toggle, fullscreen, ref } = useFullscreenElement();
  const FullscreenIcon = fullscreen ? IconArrowsMinimize : IconArrowsMaximize;

  const [scope, animate] = useAnimate();
  const animateEvent = useEffectEvent(animate);
  const mergedRef = useMergedRef(ref, scope);

  useEffect(() => {
    animateEvent(
      `.${classes.gridItem}`,
      { transform: ['translateY(var(--mantine-spacing-xs)) scale(0.98)', 'translateY(0) scale(1)'], opacity: [0, 1] },
      { duration: 0.5, ease: 'easeOut', delay: stagger(0.2) },
    );
  }, []);

  return (
    <ErrorBoundaryWrapper showRetry title="Dashboard failed to load" variant="alert">
      <DashboardExportContext value={registry}>
        <div className={classes.gridContainer} ref={mergedRef}>
          <div className={classes.header}>
            <div className={classes.title}>
              <IconBoltFilled className={classes.titleIcon} color="var(--mantine-primary-color-5)" size={16} />
              <span className={classes.titleLabel}>{spec.title}</span>
            </div>
            <div className={classes.headerActions}>
              {registry.entries.length > 0 && (
                <IconActionButton
                  classNames={{ root: classes.buttonRoot }}
                  color="dark.5"
                  label="Download data"
                  onClick={() =>
                    downloadCsvZip(
                      registry.entries.map((e) => ({ name: e.name, csv: e.getCsv() })),
                      slugify(spec.title),
                    )
                  }
                  size="xs"
                  variant="outline"
                >
                  <IconDownload size={14} />
                </IconActionButton>
              )}
              <IconActionButton
                classNames={{ root: classes.buttonRoot }}
                color={fullscreen ? 'red' : 'dark.5'}
                label={fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                onClick={toggle}
                size="xs"
              >
                <FullscreenIcon size={14} />
              </IconActionButton>
            </div>
          </div>
          <div className={classes.tiles}>
            {spec.tiles.map((tile, i) => (
              <TileRenderer
                citedMarkers={citedMarkers}
                column={tile.span.at(0)}
                // biome-ignore lint/suspicious/noArrayIndexKey: stable index is fine here because the tiles array is immutable and never reordered
                key={i}
                row={tile.span.at(1)}
                tile={tile}
              />
            ))}
          </div>
        </div>
      </DashboardExportContext>
    </ErrorBoundaryWrapper>
  );
}
